import React, { useState, ChangeEvent, Dispatch, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, reduxForm, InjectedFormProps } from "redux-form";
import { get, isEmpty, uniq } from "lodash";
import { addMonths } from "date-fns";
import { getFormattedDate, getMessage, scrollToErrorOnFormHandler, encrypt, utcDateTimeSecondToDate } from "../../../helpers/util-common";
import RenderTypeahead from "../../../shared-components/Typeahead";
import { FeatureFlag } from "../../../provider/flagFeature";
import { BallotYearsOptions } from "../../../models/common.models";
import ReduxRadio from "../../../shared-components/Radio";
import ReduxDatePicker from "../../../shared-components/DatePicker";
import RenderInputText from "../../../shared-components/InputText";
import { getActiveCommitteesFromIAAction, saveBallotHeaderAction } from "../../../redux/action/ballots";
import { LEFT_NAVIGATION_PAGE_URL } from "../../../models/navigation.models";
import { AddBallotHeaderModalProps, BallotHeaderProps } from "../interface";
import { useNavigate } from "react-router-dom";

interface I18nState {
  dictionaries: { [key: string]: any };
  currentLanguage: string;
}

interface MessagesProps {
  i18nReducer: I18nState;
}

type Props = AddBallotHeaderModalProps & InjectedFormProps<any, AddBallotHeaderModalProps>;

const AddBallotHeaderModal: React.FC<Props> = ({ showAddBallotHeaderModal, closeAddBallotHeaderModal, ballotTypes, userEmail, userId }: Props) => {
  const messageCodes = useSelector((state: MessagesProps) => get(state.i18nReducer.dictionaries, state.i18nReducer.currentLanguage));
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [ballotHeader, setBallotHeader] = useState<BallotHeaderProps>();
  const [selectedBallotType, setSelectedBallotType] = useState<string>();
  const [selectedCommitteeDesignation, setselectedCommitteeDesignation] = useState<any>();
  const [selectedBallotYear, setselectedBallotYear] = useState<string>();
  const [selectedMeetingDate, setselectedMeetingDate] = useState<Date | string>();
  const [selectedMeetingLocation, setselectedMeetingLocation] = useState<string>();

  const [invalidCommittee, setinvalidCommittee] = useState<boolean>(false);
  const [invalidBallotYear, setinvalidBallotYear] = useState<boolean>(false);
  const [invalidMeetingDate, setinvalidMeetingDate] = useState<boolean>();
  const [invalidMeetingLocation, setinvalidMeetingLocation] = useState<boolean>(false);
  const [isMeetingDateDisabled, setisMeetingDateDisabled] = useState<boolean>(false);
  const [isMeetingLocationDisabled, setisMeetingLocationDisabled] = useState<boolean>(false);
  const [ballotDateErrorMessage, setballotDateErrorMessage] = useState<number>();
  const [committeeDesignationData, setcommitteeDesignationData] = useState<Array<any>>([]);
  const [clearAllTypeaheds, setClearAllTypeaheds] = useState<boolean>(false);
  const [customClass, setCustomClass] = useState("add-ballot-header-committee");
  const maxSelectableDate = addMonths(new Date(), 18);
  const [ballotYearOptions, setBallotYearOptions] = useState(BallotYearsOptions);
  const dispatch = useDispatch();
  let navigate = useNavigate();

  useEffect(() => {
    setBallotHeader((prevState) => {
      return { ...prevState, CreatedBy: userEmail, UserId: userId };
    });
    setselectedBallotYear(new Date().getFullYear().toString());
    setSelectedBallotType("Technical");
    setBallotHeader((prevState) => {
      return { ...prevState, BallotYear: new Date().getFullYear() };
    });
    setBallotHeader((prevState) => {
      return { ...prevState, BallotTypeId: parseInt(ballotTypes.find((item) => item.BallotType === "Technical").BallotTypeId, 10) };
    });
    getCommitteeList("Technical");
    return () => {
      const yearOptions = ballotYearOptions;
      yearOptions[1].isDisabled = new Date().getMonth() < 9 ? true : false;
      setBallotYearOptions(yearOptions);
    }
  }, []);

  useEffect(() => {
    if (invalidMeetingDate) {
      setselectedMeetingDate(null);
    }
  }, [invalidMeetingDate]);

  const getCommitteeList = (ballotType: string) => {
    setSelectedBallotType(ballotType);

    getActiveCommitteesFromIAAction(dispatch, (response: any) => {
      if (response && response.length > 0) {
        const CommitteeList = uniq(response.map((item: { CommitteeDesignation: any }) => item.CommitteeDesignation));

        if (ballotType === "Technical") {
          const filteredStrings = CommitteeList.filter((str: string) => str.length > 6);
          const filteredArray = CommitteeList.filter((item) => !filteredStrings.includes(item));

          const cosfiltered = CommitteeList.filter((str: string) => str.length == 6 && str.indexOf("COS") !== -1);
          const allfilteredArray = filteredArray.filter((item) => !cosfiltered.includes(item));
          setcommitteeDesignationData(allfilteredArray);
        } else {
          setcommitteeDesignationData(CommitteeList);
        }
      }
    });
  };

  const validateMeetingLocation = () => {
    if (selectedMeetingLocation == undefined || selectedMeetingLocation?.trim() === "") {
      setinvalidMeetingLocation(true);
      setselectedMeetingLocation(selectedMeetingLocation?.trim());
      setBallotHeader((prevState) => {
        return { ...prevState, MeetingLocation: selectedMeetingLocation?.trim() };
      });
    }
  };

  const validateFormData = (data: BallotHeaderProps): boolean => {
    if (data.hasOwnProperty("MeetingDate") && new Date(data["MeetingDate"]) <= new Date()) {
      return false;
    } else if (data.hasOwnProperty("BallotTypeId") && data.hasOwnProperty("BallotYear") && data.hasOwnProperty("CommitteeDesignation") && data.hasOwnProperty("CreatedBy") && data.hasOwnProperty("MeetingDate")) {
      if (data && data.CommitteeDesignation && data.CommitteeDesignation.indexOf("COS") !== -1) {
        let inputData = ballotHeader;
        if (inputData.hasOwnProperty("MeetingLocation")) delete inputData.MeetingLocation;
        const check = Object.values(data).every((value) => value !== undefined && value !== null && value !== "");
        if (!inputData.hasOwnProperty("MeetingLocation")) inputData.MeetingLocation = null;
        return check;
      } else {
        if (data.hasOwnProperty("MeetingDate") && data.hasOwnProperty("MeetingLocation")) {
          return Object.values(data).every((value) => value !== undefined && value !== null && value !== "");
        } else return false;
      }
    } else {
      return false;
    }
  };

  const validateMeetingDate = () => {
    let dt = new Date();
    if (new Date(selectedMeetingDate) >= new Date(dt.setMonth(dt.getMonth() + 18))) {
      setballotDateErrorMessage(3);
      setinvalidMeetingDate(true);
    } else if (selectedMeetingDate == undefined) {
      setballotDateErrorMessage(2);
      setinvalidMeetingDate(true);
    } else if (new Date(selectedMeetingDate) <= new Date()) {
      setballotDateErrorMessage(1);
      setinvalidMeetingDate(true);
    }
  };

  const handleSubmit = () => {
    if (selectedBallotYear == undefined) setinvalidBallotYear(true);
    if (selectedCommitteeDesignation == undefined || selectedCommitteeDesignation?.length === 0 || selectedCommitteeDesignation[0] == undefined) {
      setinvalidCommittee(true);
    }
    if (selectedCommitteeDesignation !== undefined) {
      if (selectedCommitteeDesignation?.length == 0) {
        validateMeetingLocation();
        validateMeetingDate();
      }
      if (selectedCommitteeDesignation?.length > 0 && selectedCommitteeDesignation[0].indexOf("COS") == -1) {
        validateMeetingLocation();
        validateMeetingDate();
      }
      if (selectedCommitteeDesignation?.length > 0 && selectedCommitteeDesignation[0].indexOf("COS") !== -1) {
        validateMeetingDate();
      }
    } else {
      validateMeetingLocation();
      validateMeetingDate();
    }

    if (validateFormData(ballotHeader)) {
      setIsSubmitting(true);
      saveBallotHeaderAction(ballotHeader, dispatch, (response: any) => {
        if (response && response.BallotId && response.StatusMessage) {
          closeAddBallotHeaderModal();
          handleBallotDetailsOnClick(response.BallotId);
        } else {
          setIsSubmitting(false);
        }
      });
    }
  };

  const handleBallotDetailsOnClick = (ballotId: any) => {
    let ballotCode = encrypt(`${ballotId}`);
    navigate(`${LEFT_NAVIGATION_PAGE_URL.BALLOTDETAILS}/${ballotCode}`);
  };

  const onClose = () => {
    setIsSubmitting(false);
    closeAddBallotHeaderModal();
    onReset();
  };

  const onReset = () => {
    setSelectedBallotType("");
    setselectedCommitteeDesignation([]);
    setselectedBallotYear("");
    setselectedMeetingLocation("");
  };

  const addRemoveTypeaheadClass = (event: any) => {
    const keysToAddClassOn = ["Enter", "Backspace"];
    if (event.key === "ArrowDown") {
      event.target.value && setCustomClass("");
    } else if (keysToAddClassOn.indexOf(event.key) > -1) {
      setCustomClass("add-ballot-header-committee");
    }
  };

  const handleCommitteeSelectionInputKeyPress = (ev: { target: any; key: string }, textValue: string) => {
    if (clearAllTypeaheds) {
      setClearAllTypeaheds(false);
    }
    addRemoveTypeaheadClass(ev);
    if (ev.key === "Enter") {
      setCustomClass("add-ballot-header-committee");
      if (!isEmpty(ev.target.value)) {
        const filteredData = committeeDesignationData.filter((item: any) => item.toLowerCase().includes(ev.target.value.toLowerCase()));
        for (let i = 0; i < filteredData.length; i++) {
          validateOnChange([filteredData[i]]);
          setselectedCommitteeDesignation([filteredData[i]]);
          setBallotHeader((prevState) => {
            return { ...prevState, CommitteeDesignation: filteredData[i] };
          });
          return;
        }
      }
    }
  };

  const hanldeBallotYearSelectionRadio = (event: ChangeEvent<HTMLInputElement>) => {
    setselectedBallotYear(event.target.value);
    if (event.target.value != "") {
      setinvalidBallotYear(false);
      setBallotHeader((prevState) => {
        return { ...prevState, BallotYear: parseInt(event.target.value, 10) };
      });
    }
  };

  const handlMeetingDateChange = (value: Date, callback: Dispatch<Date | string>, field: string) => {
    let meetingDate = value;
    if (new Date(meetingDate) >= new Date(maxSelectableDate)) {
      meetingDate = null;
    }
    setinvalidMeetingDate(false);
    setballotDateErrorMessage(0);
    const formattedMeetingDate = getFormattedDate(meetingDate, "YYYY-MM-DD");
    callback(formattedMeetingDate);
    setBallotHeader((prevState) => {
      return { ...prevState, MeetingDate: formattedMeetingDate };
    });
  };

  const onMeetingLocationChange = (event: ChangeEvent<HTMLInputElement>) => {
    setselectedMeetingLocation(event.target.value);    
    setBallotHeader((prevState) => {
      return { ...prevState, MeetingLocation: event.target.value };
    });
    if (event.target.value.trim() != "") {
      setinvalidMeetingLocation(false);
    }
  };

  const ballotDateValidationMessage = (param) => {
    switch (param) {
      case 1:
        return "Meeting Date must be greater than Current Date.";
      case 2:
        return "Meeting Date is required.";
      case 3:
        return "Meeting Date must be less than 18 months.";
    }
  };

  const handlFromAndToDateChangeRaw = (event: any, field: string | Date) => {
    // This code will be added after one round of discussion
    // event?.key === "Enter" && field && advSearchButtonRef.current.click();
  };

  const validateOnChange = (value: string[]) => {
    setCustomClass("add-ballot-header-committee");
    if (clearAllTypeaheds) {
      setClearAllTypeaheds(false);
    }
    if (value[0] != "" && value[0] != null) {
      setinvalidCommittee(false);
    } else {
      setisMeetingLocationDisabled(false);
      setisMeetingDateDisabled(false);
    }
    setBallotHeader((prevState) => {
      return { ...prevState, CommitteeDesignation: value[0] };
    });
    setselectedCommitteeDesignation(value);
    if (value[0] != "" && value[0] != null) {
      if (value[0].indexOf("COS") !== -1) {
        const yearOptions = ballotYearOptions;
        yearOptions[1].isDisabled = true;
        setBallotYearOptions(yearOptions);
        setselectedBallotYear(new Date().getFullYear().toString());
        setBallotHeader((prevState) => {
          return { ...prevState, BallotYear: new Date().getFullYear() };
        });
        setisMeetingLocationDisabled(true);
        setselectedMeetingLocation("");
      } else if (value[0].indexOf("COS") == -1) {
        const yearOptions = ballotYearOptions;
        yearOptions[1].isDisabled = new Date().getMonth() < 9 ? true : false;
        setBallotYearOptions(yearOptions);
        setisMeetingLocationDisabled(false);
      }
      setinvalidMeetingDate(false);
      setinvalidMeetingLocation(false);
    }
  };

  return (
    <div className={`modal fade ${showAddBallotHeaderModal ? "show" : ""} `} id="addBallotModal" tabIndex={-1} role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered add-ballot-popup modal-lg" role="document">
        <form noValidate>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Add new ballot
              </h5>
              <button type="button" className="close" data-testid="closeAddBallotModal" data-dismiss="modal" aria-label="Close" disabled={isSubmitting} onClick={onClose}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-4">
                  {ballotTypes && (
                    <div className="form-group">
                      <label htmlFor="ballotType" className="astm-form-label">
                        <i aria-hidden="true" className="fas fa-asterisk"></i>Ballot Type
                      </label>
                      <select
                        value={selectedBallotType}
                        className="custom-select astm-custom-select"
                        data-testid="SelectBallotType"
                        onChange={(e) => {
                          let selected = e.target.value;
                          setSelectedBallotType(selected);
                          setBallotHeader((prevState) => {
                            return { ...prevState, BallotTypeId: parseInt(ballotTypes.find((item) => item.BallotType === selected).BallotTypeId, 10) };
                          });
                          getCommitteeList(selected);
                          setselectedCommitteeDesignation([]);
                          setBallotHeader((prevState) => {
                            return { ...prevState, CommitteeDesignation: "" };
                          });
                          setinvalidCommittee(false);
                          setinvalidMeetingDate(false);
                          setinvalidMeetingLocation(false);
                          setisMeetingLocationDisabled(false);
                          setClearAllTypeaheds(true);
                          setcommitteeDesignationData([]);
                        }}
                      >
                        {ballotTypes.map((option) => (
                          <option key={option.BallotTypeId} value={option.BallotType}>
                            {option.BallotType}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    {committeeDesignationData && (
                      <div className="form-group">
                        <Field name="addHeaderCommitteeDesignation" clearTypeahead={clearAllTypeaheds} disabled={committeeDesignationData?.length === 0} id="addHeaderCommitteeDesignation" required={true} label="Committee Designation" value={selectedCommitteeDesignation} component={RenderTypeahead} placeholder={committeeDesignationData?.length === 0 ? "Fetching records..." : "Type here to select"} data-testid="addHeaderCommitteeDesignationSearch" customClass={customClass} fieldError={invalidCommittee} options={committeeDesignationData} selected={selectedCommitteeDesignation} defaultValue={selectedCommitteeDesignation} change={validateOnChange} keyDown={handleCommitteeSelectionInputKeyPress} />
                        {invalidCommittee && (
                          <div id="helpText" className="invalid-feedback block">
                            {"Committee is required."}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group custom-radio-block">
                    <label htmlFor="ballotYear">
                      <i aria-hidden="true" className="fas fa-asterisk"></i>Ballot Year
                    </label>
                    <Field type="radio" name="ballotYear" options={ballotYearOptions} component={ReduxRadio} dataTestId="ballot-years-radio" title={getMessage(messageCodes, "1506.text")} value={selectedBallotYear} selectedValue={selectedBallotYear} onChange={hanldeBallotYearSelectionRadio} />
                    {invalidBallotYear && (
                      <div id="helpText" className="invalid-feedback block">
                        {"Ballot year is required."}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <Field required component={ReduxDatePicker} name="MeetingDate" autoComplete="off" label="Meeting Date" maxDate={maxSelectableDate} className="date-input" placeholder="MM/DD/YYYY" fieldError={invalidMeetingDate} value={selectedMeetingDate && utcDateTimeSecondToDate(selectedMeetingDate)} defaultValue={selectedMeetingDate && utcDateTimeSecondToDate(selectedMeetingDate)} monthChangeHandler={(value: Date) => handlMeetingDateChange(value, setselectedMeetingDate, "meetingDate")} yearChangeHandler={(value: Date) => handlMeetingDateChange(value, setselectedMeetingDate, "meetingDate")} changeHandler={(value: Date) => handlMeetingDateChange(value, setselectedMeetingDate, "meetingDate")} onChangeRawHandler={(event: ChangeEvent<HTMLInputElement>) => handlFromAndToDateChangeRaw(event, selectedMeetingDate)} isDisabled={isMeetingDateDisabled} />
                    {invalidMeetingDate && (
                      <div id="helpText" className="invalid-feedback block">
                        {ballotDateValidationMessage(ballotDateErrorMessage)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <Field required label="Meeting Location" name="MeetingLocation" id="MeetingLocation" component={RenderInputText} type={"text"} value={selectedMeetingLocation} defaultvalue={selectedMeetingLocation} placeholder="Type Meeting Location" fieldError={invalidMeetingLocation} onChange={onMeetingLocationChange} maxlength={100} disabled={isMeetingLocationDisabled}></Field>
                    {invalidMeetingLocation && (
                      <div id="helpText" className="invalid-feedback block">
                        {"Meeting Location is required."}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn astm-btn btn-light" disabled={isSubmitting} onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn astm-btn btn-secondary" data-dismiss="modal" data-testid="AddNewBallotSubmit" disabled={isSubmitting} onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default reduxForm<any, AddBallotHeaderModalProps>({
  form: "AddBallotHeaderModal",
  onSubmitFail: () => scrollToErrorOnFormHandler(),
})(AddBallotHeaderModal);
